import get from "lodash/get";
import React, {Component} from "react";
import PropTypes from "prop-types";
import compose from "lodash/flowRight";
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import {withTranslation, Constants} from "@unibuddy/intl";
import {Query} from "@apollo/client/react/components";
import {Spinner, VisuallyHidden, Box} from "@unibuddy/patron";

import {productSpec} from "shared/AnalyticsNew/constants/productSpecification";
import {withRouteParamTheme} from "shared/Styling/hocs/withTheme";
import RecordAnalytics from "shared/Analytics/RecordAnalytics";
import withAnalytics from "shared/AnalyticsNew/withAnalytics/withAnalytics";
import Auth from "shared/Auth/auth";
import withAuth from "shared/Auth/withAuth/withAuth";
import {MentorCarousel} from "shared/Widgets/MentorCarousel/components/MentorCarousel/MentorCarousel";
import {isFeatureEnabled} from "shared/Utils/FeatureUtils";
import {PageTitle} from "../../../components/PageTitle/PageTitle";
import mentorListQuery from "../Mentor/queries/mentorListQuery";
import MentorPreviewCard from "../../../widgets/modules/Mentor/components/MentorPreviewCard/MentorPreviewCard";
import buddiesQuery from "../Buddies/queries/buddiesQuery";
import {StaffBuddycard} from "../BuddiesNew/containers/StaffBuddycard";
import {IntlNamespaces} from "../../../translations/types";
import {INTL_BUDDIES} from "../../../translations/common";
import {sequenceFilterMentorListQuery} from "../Mentor/queries/sequenceFilterMentorListQuery";

export class ExternalFilteredMentorListComponent extends Component {
    componentDidMount() {
        const filter = get(this.props, "match.params.filter", "");
        const isStaff = ["role", "department", "staff"].includes(filter);
        RecordAnalytics.page(this.props, "External Buddy Card List"); // Legacy Events
        this.props.analytics.events.pageViewed({
            prefix: this.props.analytics.pageNames.buddiesList,
        });
        const productName = this.props.isCarousel
            ? productSpec.embeddedExternalBuddyCardsCarousel.NAME
            : productSpec.embeddedExternalBuddyCards.NAME;
        const {isProductOpenedEventEnabled} = this.props;
        if (isProductOpenedEventEnabled) {
            this.props.analytics.events.productOpened({
                name: productName,
                product: `${productName}${isStaff ? " - Staff" : ""}`,
                universitySlug: this.props.match.params.universitySlug,
                ubLang: this.props?.i18n?.language ?? Constants.DEFAULT_LOCALE,
            });
        }
    }

    componentDidUpdate(prevProps) {
        const filter = get(this.props, "match.params.filter", "");
        const isStaff = ["role", "department", "staff"].includes(filter);
        const productName = this.props.isCarousel
            ? productSpec.embeddedExternalBuddyCardsCarousel.NAME
            : productSpec.embeddedExternalBuddyCards.NAME;
        if (prevProps.isProductOpenedEventEnabled !== this.props.isProductOpenedEventEnabled) {
            this.props.analytics.events.productOpened({
                name: productName,
                product: `${productName}${isStaff ? " - Staff" : ""}`,
                universitySlug: this.props.match.params.universitySlug,
                ubLang: this.props?.i18n?.language ?? Constants.DEFAULT_LOCALE,
            });
        }
    }

    onChatNow = (buddyId, buddyPosition) => {
        RecordAnalytics.event(this.props, "Buddy Card Chat Now Clicked", {buddyId, buddyPosition});
        const path = this.props.location.pathname.split("/").slice(0, 5).join("/");
        if (Auth.loggedIn(this.props.authState))
            this.props.history.push(`${path}/inbox/start/${buddyId}`);
        else this.props.history.push(`${path}/inbox/pre/${buddyId}`);
    };

    renderMentors(mentors) {
        return mentors.map((mentor, i) => (
            <Box maxW="400px" marginBottom="medium" marginX="auto" key={mentor.id}>
                <MentorPreviewCard
                    mentor={mentor}
                    onChatNow={this.onChatNow.bind(this, mentor.id, i + 1)}
                    rootUrl={this.props.location.pathname}
                    buddyPosition={i + 1}
                    university={this.props.university}
                />
            </Box>
        ));
    }

    renderStaff(staff) {
        const staffCards = staff.map((staffMem) => {
            staffMem = {
                ...staffMem,
                staffBuddyName: `${staffMem.firstName} (staff)`,
            };
            return (
                <Box maxW="400px" marginBottom="medium" marginX="auto" key={staffMem.id}>
                    <StaffBuddycard
                        buddy={staffMem}
                        onChatNow={this.onChatNow.bind(this, staffMem.id)}
                        rootUrl={this.props.location.pathname}
                    />
                </Box>
            );
        });
        return staffCards;
    }

    render() {
        const filter = get(this.props, "match.params.filter", "");
        const filterVal = get(this.props, "match.params.value", "");
        const isStaff = ["role", "department", "staff"].includes(filter);
        const universitySlug = get(this.props, "match.params.universitySlug", "");
        const isDegreeTypesEnabled = isFeatureEnabled("enableDegreeTypes", this.props.university);
        let query;
        let variables;
        let domesticOrInternational = null;
        const sequenceFilter = [];

        if (filter !== "mentorId" && !isStaff) {
            let applyDegreeFilter = false;

            if (filter === "domesticOrInternational") {
                domesticOrInternational = filterVal;
            } else {
                const filterKeyMap = {
                    level: "degree_level",
                    degreeId: "degree",
                    countryId: "country",
                };
                const filterKey = filterKeyMap[filter];

                // sequenceFilter should be of type [{field: string, value: string[]}]
                // or can be an empty array if domesticOrInternational has a value
                if (filterKey) sequenceFilter.push({field: filterKey, value: filterVal});
                if (filterKey && filterKey === "degree") applyDegreeFilter = true;
            }

            query = sequenceFilterMentorListQuery;
            variables = {
                universitySlug,
                sequenceFilter,
                applyDegreeFilter,
                domesticOrInternational,
                isDegreeTypesEnabled,
                limit: 4,
            };
        } else if (filter === "mentorId" && !isStaff) {
            query = mentorListQuery;
            variables = {
                universitySlug,
                [filter]: filterVal,
                isDegreeTypesEnabled,
            };
        } else if (isStaff) {
            query = buddiesQuery;
            variables = {
                universitySlug,
                idIn: filter === "staff" ? [filterVal] : null,
                role: "staff",
                staffFields: filter !== "staff" ? JSON.stringify({[filter]: filterVal}) : "{}",
            };
        }

        const isStaffProfilesEnabled = isFeatureEnabled("staffProfiles", this.props.university);

        return (
            <div id="mentors-container">
                <PageTitle title={this.props.t(INTL_BUDDIES.key, INTL_BUDDIES.defaultValue)} />
                <Query query={query} variables={variables} skip={!universitySlug}>
                    {({loading, data, error}) => {
                        if (error) console.log({error});
                        if (loading || !data) {
                            return (
                                <div data-test-id="query-loading">
                                    <VisuallyHidden>Loading</VisuallyHidden>
                                    <Spinner color="#E4E8EB" aria-hidden />
                                </div>
                            );
                        }
                        const mentors =
                            data?.mentorList?.mentors ??
                            data?.sequenceFilterMentorList?.mentors ??
                            [];
                        const staff = data?.buddies?.users ?? [];

                        if (this.props.isCarousel) {
                            return (
                                <MentorCarousel
                                    loading={loading}
                                    mentors={mentors}
                                    onChatNow={this.onChatNow}
                                    showSeeMore={false}
                                    isExternal
                                />
                            );
                        }

                        return isStaff
                            ? isStaffProfilesEnabled && this.renderStaff(staff)
                            : this.renderMentors(mentors);
                    }}
                </Query>
            </div>
        );
    }
}

ExternalFilteredMentorListComponent.propTypes = {
    isCarousel: PropTypes.bool,
};

ExternalFilteredMentorListComponent.defaultProps = {
    isCarousel: false,
};

export default compose(
    withRouter,
    connect(),
    withAuth,
    withAnalytics,
    withRouteParamTheme(),
    withTranslation(IntlNamespaces.COMMON),
)(ExternalFilteredMentorListComponent);
